/**
 * Lectura del estatus que devuelve el ConsultaCFDIService del SAT: de las
 * cadenas crudas del webservice a texto, badges y la decisión de si el estatus
 * local difiere del oficial.
 *
 * Sin DOM y sin jQuery a propósito: es la parte que se puede probar y la que
 * decide si al usuario se le ofrece el botón de "Actualizar estatus local".
 * El HTML del modal vive en la vista (noble/documentos/modals/modal-estatus-sat.php).
 *
 * Convención de códigos, la misma de la base (`cfdi.estado_cfdi`,
 * `cfdi_cancelacion.estado_sat`): 1 = Vigente, 0 = Cancelado, null = sin dato.
 */

export var VIGENTE = 1;
export var CANCELADO = 0;

/** Estado del documento. Los textos son los que responde el SAT, tal cual. */
var ESTADOS = {
    'Vigente':      { texto: 'Vigente',      clase: 'badge bg-success',   icono: 'ti-circle-check' },
    'Cancelado':    { texto: 'Cancelado',    clase: 'badge bg-danger',    icono: 'ti-circle-x' },
    'No Encontrado':{ texto: 'No encontrado',clase: 'badge bg-secondary', icono: 'ti-help-circle' }
};

var ESTADO_DESCONOCIDO = { texto: 'Sin dato', clase: 'badge bg-secondary', icono: 'ti-help-circle' };

/**
 * EsCancelable. "Cancelable con aceptación" es el caso que obliga a esperar:
 * el receptor tiene 72 horas para aceptar o rechazar, así que el estatus de
 * hoy no es el definitivo y conviene decírselo al usuario.
 */
var CANCELABLE = {
    'Cancelable sin aceptación': {
        texto: 'Cancelable sin aceptación',
        clase: 'badge bg-light text-dark',
        nota: 'Se puede cancelar de forma directa, sin pedirle nada al receptor.'
    },
    'Cancelable con aceptación': {
        texto: 'Cancelable con aceptación',
        clase: 'badge bg-warning',
        nota: 'El receptor tiene 72 horas para aceptar o rechazar. El estatus todavía puede cambiar.'
    },
    'No cancelable': {
        texto: 'No cancelable',
        clase: 'badge bg-light text-dark',
        nota: 'El SAT lo marca como no cancelable; suele pasar cuando tiene CFDIs relacionados vigentes.'
    }
};

/** EstatusCancelacion. Solo trae valor cuando hubo una solicitud de cancelación. */
var CANCELACION = {
    'En proceso': {
        texto: 'En proceso',
        clase: 'badge bg-warning',
        nota: 'Hay una solicitud de cancelación esperando la respuesta del receptor. '
             + 'Tiene 72 horas para aceptarla o rechazarla; hasta entonces el estatus puede cambiar.'
    },
    'Cancelado sin aceptación': {
        texto: 'Cancelado sin aceptación',
        clase: 'badge bg-danger',
        nota: ''
    },
    'Cancelado con aceptación': {
        texto: 'Cancelado con aceptación',
        clase: 'badge bg-danger',
        nota: ''
    },
    'Plazo vencido': {
        texto: 'Plazo vencido',
        clase: 'badge bg-danger',
        nota: 'El receptor no respondió en 72 horas y el SAT dio la cancelación por buena.'
    },
    'Solicitud rechazada': {
        texto: 'Solicitud rechazada',
        clase: 'badge bg-secondary',
        nota: 'El receptor rechazó la cancelación: el CFDI sigue vigente.'
    }
};

function limpia(valor) {
    return (valor === null || valor === undefined) ? '' : String(valor).trim();
}

/** Badge del estado que reporta el SAT, desde la cadena cruda del webservice. */
export function badgeEstadoSat(estado) {
    return ESTADOS[limpia(estado)] || ESTADO_DESCONOCIDO;
}

/** Badge del estado guardado en la base, desde el código 1/0. */
export function badgeEstadoLocal(codigo) {
    if (codigo === null || codigo === undefined || codigo === '') { return ESTADO_DESCONOCIDO; }
    return Number(codigo) === CANCELADO ? ESTADOS['Cancelado'] : ESTADOS['Vigente'];
}

/** EsCancelable en texto + badge; null cuando el SAT no lo informó. */
export function badgeCancelable(esCancelable) {
    return CANCELABLE[limpia(esCancelable)] || null;
}

/** EstatusCancelacion en texto + badge; null cuando no hay solicitud en curso. */
export function badgeEstatusCancelacion(estatusCancelacion) {
    return CANCELACION[limpia(estatusCancelacion)] || null;
}

/**
 * ¿El estatus local difiere del que reporta el SAT?
 *
 * Solo hay diferencia entre dos estados CONOCIDOS. Si el SAT no encontró el
 * comprobante (null) no hay nada que comparar: eso no es una discrepancia, es
 * ausencia de dato, y tratarla como diferencia llevaría a "corregir" un CFDI
 * bueno contra una respuesta que no dice nada.
 */
export function difiere(estadoLocal, estadoSat) {
    if (estadoLocal === null || estadoLocal === undefined || estadoLocal === '') { return false; }
    if (estadoSat === null || estadoSat === undefined || estadoSat === '') { return false; }
    return Number(estadoLocal) !== Number(estadoSat);
}

/**
 * Qué se le puede ofrecer al usuario a partir de la diferencia.
 *
 * 'cancelar_local'  el SAT lo tiene cancelado y aquí sigue vigente
 * 'reactivar_local' aquí está cancelado y el SAT lo tiene vigente
 * 'ninguna'         coinciden, o falta un dato para comparar
 */
export function accionSugerida(estadoLocal, estadoSat) {
    if (!difiere(estadoLocal, estadoSat)) { return 'ninguna'; }
    return Number(estadoSat) === CANCELADO ? 'cancelar_local' : 'reactivar_local';
}

/** Texto del botón/confirmación según la acción. */
export function textoAccion(accion) {
    if (accion === 'cancelar_local') {
        return {
            boton: 'Marcar como cancelado',
            titulo: '¿Lo marcamos como cancelado?',
            detalle: 'El SAT tiene este CFDI como cancelado y aquí sigue como vigente. '
                   + 'Al confirmar, su estatus en FacturaDigital queda igual al del SAT.'
        };
    }
    if (accion === 'reactivar_local') {
        return {
            boton: 'Marcar como vigente',
            titulo: '¿Lo marcamos como vigente?',
            detalle: 'El SAT tiene este CFDI como vigente y aquí está marcado como cancelado. '
                   + 'Al confirmar, su estatus en FacturaDigital queda igual al del SAT.'
        };
    }
    return null;
}

/**
 * Modelo de vista completo del modal, a partir de la respuesta del endpoint
 * v2/documentos/verificar_estatus_sat_ajax.
 *
 * Se devuelve `puedeActualizar` como campo aparte de `difiere` porque el botón
 * exige además que el SAT haya encontrado el comprobante y que el CFDI no sea
 * de pruebas: son tres condiciones y conviene que el modal lea una sola.
 */
export function resumen(payload) {
    payload = payload || {};
    var sat = payload.sat || {};
    var local = payload.local || {};
    var aplica = payload.aplica !== false;
    var encontrado = aplica && sat.encontrado === true;

    var estadoLocal = (local.estado === null || local.estado === undefined) ? null : Number(local.estado);
    var estadoSat = encontrado && sat.estado_codigo !== null && sat.estado_codigo !== undefined
        ? Number(sat.estado_codigo)
        : null;

    var hayDiferencia = difiere(estadoLocal, estadoSat);
    var accion = accionSugerida(estadoLocal, estadoSat);

    return {
        aplica: aplica,
        encontrado: encontrado,
        local: badgeEstadoLocal(estadoLocal),
        estadoLocal: estadoLocal,
        sat: encontrado ? badgeEstadoSat(sat.estado) : ESTADOS['No Encontrado'],
        estadoSat: estadoSat,
        cancelable: encontrado ? badgeCancelable(sat.es_cancelable) : null,
        cancelacion: encontrado ? badgeEstatusCancelacion(sat.estatus_cancelacion) : null,
        fechaConsulta: limpia(sat.fecha_consulta),
        difiere: hayDiferencia,
        accion: accion,
        textos: textoAccion(accion),
        puedeActualizar: aplica && encontrado && hayDiferencia,
        aviso: avisoContexto(aplica, encontrado, sat)
    };
}

/**
 * La línea que explica por qué el resultado es el que es. Los cuatro casos que
 * más confunden: CFDI de pruebas, comprobante que el SAT no reconoce, respuesta
 * del SAT con un Estado que no está en el catálogo, y cancelación con
 * aceptación todavía en proceso.
 */
export function avisoContexto(aplica, encontrado, sat) {
    sat = sat || {};

    if (!aplica) {
        return {
            tipo: 'info',
            texto: 'Este CFDI se timbró en modo de pruebas, así que no existe en el SAT '
                 + 'y no hay estatus oficial que consultar.'
        };
    }
    if (!encontrado) {
        return {
            tipo: 'warning',
            texto: 'El SAT no encontró este comprobante. Suele pasar cuando el timbrado aún no se refleja '
                 + '(puede tardar unas horas) o cuando el RFC o el total no coinciden con los del XML.'
        };
    }

    // El SAT contestó y sí reconoce el comprobante, pero con un Estado fuera de
    // {Vigente, Cancelado}. Sin esta rama el modal deja el badge en "Sin dato" y
    // no explica nada, que es peor que decir que la respuesta vino rara: el
    // usuario no sabe si el problema es su CFDI o el webservice.
    if (sat.estado_codigo === null || sat.estado_codigo === undefined) {
        var crudo = limpia(sat.estado);
        return {
            tipo: 'warning',
            texto: 'El SAT respondió con un estado que no reconocemos'
                 + (crudo ? ': "' + crudo + '"' : '')
                 + '. No podemos compararlo con el estatus local, así que no te ofrecemos cambiarlo.'
        };
    }

    var cancelacion = badgeEstatusCancelacion(sat.estatus_cancelacion);
    if (cancelacion && cancelacion.nota && limpia(sat.estatus_cancelacion) === 'En proceso') {
        return { tipo: 'warning', texto: cancelacion.nota };
    }

    var cancelable = badgeCancelable(sat.es_cancelable);
    if (cancelable && limpia(sat.es_cancelable) === 'Cancelable con aceptación') {
        return { tipo: 'info', texto: cancelable.nota };
    }

    return null;
}
